import { networkManager } from '../config/NetworkManager';
import { setLoaderControl as initializeLoaderControl } from '../config/loaderControl';
import { authenticatedFetch, waitForClerkSessionToken } from './authHeaders';

/** GET /generated-file — shared by chat.fetchGeneratedFileBlob and legacy download. */
const fetchGeneratedFileBlobRequest = async (path) => {
  const response = await authenticatedFetch(
    `${networkManager.EDS_BACKEND_URL}/generated-file?path=${encodeURIComponent(path)}`,
    { method: 'GET' }
  );
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.detail || `Failed to download generated file: ${response.status}`);
  }
  return response.blob();
};

const api = {
  // Oracle Forms Modernization Chat API
  chat: {
    sendQuery: async (query, attachments, options = {}) => {
      // Don't use withLoader here as ChatInterface has its own loading state
      const payload = { query };
      if (options.moduleType) {
        payload.module_type = options.moduleType;
      }
      if (options.jobId) {
        payload.job_id = options.jobId;
      }
      if (Array.isArray(attachments) && attachments.length > 0) {
        payload.attachments = attachments.map((a) => {
          const row = {
            filename: String(a.filename || a.name || 'attachment.txt'),
            // Always include content (JSON.stringify drops undefined; backend needs the field).
            content: a.content != null ? String(a.content) : '',
          };
          if (a.contentEncoding === 'base64' || a.content_encoding === 'base64') {
            row.content_encoding = 'base64';
          }
          return row;
        });
      }
      const response = await authenticatedFetch(`${networkManager.EDS_BACKEND_URL}/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Request failed with status ${response.status}`);
      }

      return response.json();
    },

    /** Per-user generation quota for onboarding UI (module-scoped). */
    getGenerationQuota: async (moduleType) => {
      const token = await waitForClerkSessionToken();
      if (!token) {
        const err = new Error('Clerk session token is not ready');
        err.code = 'CLERK_TOKEN_NOT_READY';
        throw err;
      }
      const params = new URLSearchParams({
        moduleType: moduleType || 'tech-doc',
      });
      const response = await authenticatedFetch(
        `${networkManager.EDS_BACKEND_URL}/generation-quota?${params}`,
        { method: 'GET', headers: { 'Content-Type': 'application/json' } }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const err = new Error(
          errorData?.detail || `Failed to fetch generation quota: ${response.status}`
        );
        err.status = response.status;
        throw err;
      }
      const data = await response.json();
      return {
        ...data,
        authenticated: true,
        remaining: data.remaining_generations,
        limit: data.generations_limit,
        used: data.generations_used,
      };
    },

    /** Raw file bytes from GET /generated-file (optional; most flows use downloadGeneratedFile). */
    fetchGeneratedFileBlob: fetchGeneratedFileBlobRequest,

    /** Triggers a normal browser file download (typically the Downloads folder). */
    downloadGeneratedFile: async (path, fallbackName = 'generated-file') => {
      const blob = await fetchGeneratedFileBlobRequest(path);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fallbackName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { status: 'success' };
    },

    // Conversion Test Report APIs
    listReportTypes: async () => {
      const response = await authenticatedFetch(`${networkManager.QA_AGENT_URL}/reports/types`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch report types: ${response.status}`);
      }

      return response.json();
    },

    listReports: async (reportType = null, formName = null) => {
      let url = `${networkManager.QA_AGENT_URL}/reports/list`;
      const params = new URLSearchParams();
      
      if (reportType) {
        params.append('report_type', reportType);
      }
      if (formName) {
        params.append('form_name', formName);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch reports: ${response.status}`);
      }

      return response.json();
    },

    getReportByTypeAndForm: async (reportType, formName, fileType = 'html') => {
      const url = `${networkManager.QA_AGENT_URL}/reports/${encodeURIComponent(reportType)}/${encodeURIComponent(formName)}?file_type=${fileType}`;
      
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': fileType === 'html' ? 'text/html' : 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = fileType === 'html' 
          ? await response.text().catch(() => null)
          : await response.json().catch(() => null);
        throw new Error(errorData?.detail || errorData || `Failed to fetch report: ${response.status}`);
      }

      return fileType === 'html' ? response.text() : response.json();
    },

    getReportInfo: async (reportType, formName) => {
      const url = `${networkManager.QA_AGENT_URL}/reports/${encodeURIComponent(reportType)}/${encodeURIComponent(formName)}/info`;
      
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch report info: ${response.status}`);
      }

      return response.json();
    },

    // Legacy endpoints for backward compatibility
    getReport: async (reportName, category = null) => {
      const url = category
        ? `${networkManager.QA_AGENT_URL}/reports/legacy/${encodeURIComponent(reportName)}?category=${encodeURIComponent(category)}`
        : `${networkManager.QA_AGENT_URL}/reports/legacy/${encodeURIComponent(reportName)}`;
      
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/html',
        },
      });

      if (!response.ok) {
        const errorData = await response.text().catch(() => null);
        throw new Error(errorData || `Failed to fetch report: ${response.status}`);
      }

      return response.text();
    },

    // Conversion Checkpoints APIs
    listCheckpointForms: async () => {
      const response = await authenticatedFetch(`${networkManager.CHECKPOINTS_URL}/checkpoints/forms`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch forms: ${response.status}`);
      }

      return response.json();
    },

    getFormCheckpoints: async (formName, moduleType, jobId = null) => {
      const params = new URLSearchParams({ moduleType: moduleType || 'brd' });
      if (jobId) {
        params.set('jobId', jobId);
      }
      const response = await authenticatedFetch(
        `${networkManager.CHECKPOINTS_URL}/checkpoints/forms/${encodeURIComponent(formName)}?${params}`,
        {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch checkpoints: ${response.status}`);
      }

      return response.json();
    },

    getAllCheckpoints: async () => {
      const response = await authenticatedFetch(`${networkManager.CHECKPOINTS_URL}/checkpoints/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch all checkpoints: ${response.status}`);
      }

      return response.json();
    },

    getRecentCheckpoints: async (minutes = 5, moduleType, jobId = null) => {
      const params = new URLSearchParams({
        minutes: String(minutes),
        moduleType: moduleType || 'brd',
      });
      if (jobId) {
        params.set('jobId', jobId);
      }
      const response = await authenticatedFetch(
        `${networkManager.CHECKPOINTS_URL}/checkpoints/recent?${params}`,
        {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch recent checkpoints: ${response.status}`);
      }

      return response.json();
    },

    listTestCases: async () => {
      const response = await authenticatedFetch(`${networkManager.QA_AGENT_URL}/testcases/list`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch test cases: ${response.status}`);
      }

      return response.json();
    },

    downloadTestCase: async (testcaseName) => {
      const response = await authenticatedFetch(`${networkManager.QA_AGENT_URL}/testcases/${encodeURIComponent(testcaseName)}/download`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to download test case: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = testcaseName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { status: 'success', message: `Test case '${testcaseName}' downloaded successfully` };
    },
  },

  artifacts: {
    getBackendContents: async (path = '') => {
      const params = path ? `path=${encodeURIComponent(path)}` : '';
      const response = await authenticatedFetch(`${networkManager.ARTIFACTS_URL || networkManager.EDS_BACKEND_URL}/artifacts/backend/contents?${params}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to fetch artifacts (${response.status})`);
      }
      return response.json();
    },
    getFrontendContents: async (path = '') => {
      const params = path ? `path=${encodeURIComponent(path)}` : '';
      const response = await authenticatedFetch(`${networkManager.ARTIFACTS_URL || networkManager.EDS_BACKEND_URL}/artifacts/frontend/contents?${params}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to fetch artifacts (${response.status})`);
      }
      return response.json();
    },
    getBackendFile: async (path) => {
      const response = await authenticatedFetch(`${networkManager.ARTIFACTS_URL || networkManager.EDS_BACKEND_URL}/artifacts/backend/file?path=${encodeURIComponent(path)}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to fetch file (${response.status})`);
      }
      return response.json();
    },
    getFrontendFile: async (path) => {
      const response = await authenticatedFetch(`${networkManager.ARTIFACTS_URL || networkManager.EDS_BACKEND_URL}/artifacts/frontend/file?path=${encodeURIComponent(path)}`);
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Failed to fetch file (${response.status})`);
      }
      return response.json();
    },
    editFile: async (content, prompt, filename, source) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 min for long LLM edits
      try {
        const response = await authenticatedFetch(`${networkManager.ARTIFACTS_URL || networkManager.EDS_BACKEND_URL}/artifacts/edit`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content, prompt, filename, source }),
          signal: controller.signal,
        });
        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          const msg = Array.isArray(errBody.detail) ? errBody.detail.map((e) => e?.msg || e).join('; ') : (errBody.detail || errBody.message);
          throw new Error(msg || `Edit failed (${response.status})`);
        }
        const data = await response.json();
        if (data == null || typeof data.content !== 'string') {
          throw new Error('Edit API returned invalid response (missing content)');
        }
        return data;
      } finally {
        clearTimeout(timeoutId);
      }
    },
    commitFile: async (path, content, source, commitMessage) => {
      const response = await authenticatedFetch(`${networkManager.ARTIFACTS_URL || networkManager.EDS_BACKEND_URL}/artifacts/commit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path, content, source, commit_message: commitMessage }),
      });
      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || errBody.message || `Commit failed (${response.status})`);
      }
      return response.json();
    },
  },
};

// Export the API and the init function
export default api;
export { initializeLoaderControl };